import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  DollarSign, 
  CheckCircle2, 
  ArrowRight,
  X,
  Upload,
  Image,
  Loader2,
  Globe,
  EyeOff,
  Calendar,
  Clock
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "@/App";

// Common timezones list
const TIMEZONES = [
  { value: "UTC", label: "UTC (Coordinated Universal Time)" },
  { value: "America/New_York", label: "Eastern Time (ET) - New York" },
  { value: "America/Chicago", label: "Central Time (CT) - Chicago" },
  { value: "America/Denver", label: "Mountain Time (MT) - Denver" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT) - Los Angeles" },
  { value: "Europe/London", label: "GMT - London" },
  { value: "Europe/Paris", label: "CET - Paris, Berlin" },
  { value: "Europe/Moscow", label: "MSK - Moscow" },
  { value: "Asia/Dubai", label: "GST - Dubai" },
  { value: "Asia/Kolkata", label: "IST - India" },
  { value: "Asia/Singapore", label: "SGT - Singapore" },
  { value: "Asia/Hong_Kong", label: "HKT - Hong Kong" },
  { value: "Asia/Tokyo", label: "JST - Tokyo" },
  { value: "Asia/Shanghai", label: "CST - Shanghai" },
  { value: "Australia/Sydney", label: "AEST - Sydney" },
  { value: "Pacific/Auckland", label: "NZST - Auckland" },
];

// Availability options
const AVAILABILITY_OPTIONS = [
  { value: "few_hours", label: "Few hours a month for expert sessions" },
  { value: "part_time", label: "Open for part time long term assignments" },
  { value: "full_time", label: "Open for full time roles" },
];

const CURRENCIES = [
  { value: "USD", label: "USD ($) - US Dollar", symbol: "$" },
  { value: "EUR", label: "EUR (€) - Euro", symbol: "€" },
  { value: "GBP", label: "GBP (£) - British Pound", symbol: "£" },
  { value: "AED", label: "AED - UAE Dirham", symbol: "AED" },
  { value: "INR", label: "INR (₹) - Indian Rupee", symbol: "₹" },
  { value: "SAR", label: "SAR - Saudi Riyal", symbol: "SAR" },
  { value: "QAR", label: "QAR - Qatari Riyal", symbol: "QAR" },
  { value: "KWD", label: "KWD - Kuwaiti Dinar", symbol: "KWD" },
  { value: "BHD", label: "BHD - Bahraini Dinar", symbol: "BHD" },
  { value: "OMR", label: "OMR - Omani Rial", symbol: "OMR" },
];

export default function BecomeExpertPage() {
  const navigate = useNavigate();
  const { user, fetchUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState({ industries: [], expertise: [] });
  const [imageOption, setImageOption] = useState("upload"); // "upload" or "url"
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: "",
    bio: "",
    expertise: [],
    industries: [],
    hourly_rate: "",
    currency: "USD",
    meeting_link: "",
    linkedin: "",
    image_url: "",
    timezone: "Asia/Dubai",
    stay_anonymous: false,
    google_calendar_connected: false,
    availability: [],
  });
  const [calendarConnecting, setCalendarConnecting] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleExpertiseSelect = (exp) => {
    if (exp && !formData.expertise.includes(exp)) {
      setFormData(prev => ({ ...prev, expertise: [...prev.expertise, exp] }));
    }
  };

  const removeExpertise = (exp) => {
    setFormData(prev => ({ ...prev, expertise: prev.expertise.filter(e => e !== exp) }));
  };

  const handleIndustrySelect = (ind) => {
    if (ind && !formData.industries.includes(ind)) {
      setFormData(prev => ({ ...prev, industries: [...prev.industries, ind] }));
    }
  };

  const removeIndustry = (ind) => {
    setFormData(prev => ({ ...prev, industries: prev.industries.filter(i => i !== ind) }));
  };

  const handleConnectCalendar = async () => {
    if (!user) {
      toast.error("Please login first to connect your calendar");
      return;
    }
    
    setCalendarConnecting(true);
    try {
      const response = await axios.get(`${API}/auth/google/calendar/login`);
      // Redirect to Google OAuth
      window.location.href = response.data.authorization_url;
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to connect calendar";
      toast.error(message);
      if (message.includes("not configured")) {
        toast.info("Google Calendar integration requires setup. Contact admin.");
      }
    } finally {
      setCalendarConnecting(false);
    }
  };

  const toggleAvailability = (value) => {
    setFormData(prev => ({
      ...prev,
      availability: prev.availability.includes(value)
        ? prev.availability.filter(v => v !== value)
        : [...prev.availability, value]
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload JPEG, PNG, WebP, or GIF");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 5MB");
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      const response = await axios.post(`${API}/upload/image`, formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setFormData(prev => ({ ...prev, image_url: response.data.url }));
      toast.success("Image uploaded successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error("Please login to apply as an expert");
      navigate("/login");
      return;
    }

    if (!formData.title || !formData.bio || formData.expertise.length === 0 || !formData.hourly_rate) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/experts/apply`, {
        ...formData,
        hourly_rate: parseFloat(formData.hourly_rate),
      });
      
      await fetchUser();
      toast.success("Expert profile created successfully!");
      
      // Navigate to success page with expert data for LinkedIn sharing
      navigate("/expert-onboarding-success", { 
        state: { 
          expertData: {
            id: response.data.expert_id,
            name: user?.name,
            title: formData.title,
            expertise: formData.expertise
          }
        }
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create expert profile");
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { title: "Set Your Own Rates", description: "You decide what your time is worth" },
    { title: "Flexible Schedule", description: "Accept bookings when it works for you" },
    { title: "Global Reach", description: "Connect with clients worldwide" },
    { title: "Easy Payments", description: "Get paid directly to your account" },
  ];

  return (
    <div className="min-h-screen bg-obsidian">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-500 mb-4">BECOME AN EXPERT</p>
            <h1 className="font-display text-5xl md:text-6xl mb-6">
              Share Your Expertise,<br />
              <span className="text-gold-gradient">Get Paid</span>
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed">
              Join our network of world-class advisors. Help founders and professionals 
              succeed while earning on your own terms.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 border-y border-slate-800/50 bg-charcoal/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <CheckCircle2 className="w-8 h-8 text-gold-500 mx-auto mb-3" />
                <h3 className="font-semibold mb-1">{benefit.title}</h3>
                <p className="text-sm text-slate-500">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2 className="font-display text-3xl mb-8">Create Your Expert Profile</h2>
            
            {!user && (
              <div className="bg-charcoal border border-slate-800 p-6 rounded-sm mb-8">
                <p className="text-slate-400 mb-4">
                  You need to be logged in to create an expert profile.
                </p>
                <div className="flex gap-4">
                  <Button 
                    onClick={() => navigate("/login")}
                    className="bg-gold-500 hover:bg-gold-400 text-black"
                  >
                    Sign In
                  </Button>
                  <Button 
                    onClick={() => navigate("/register")}
                    variant="outline"
                    className="border-slate-700"
                  >
                    Create Account
                  </Button>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Professional Title */}
              <div className="space-y-2">
                <Label>Professional Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Former VP at Google, 3x Founder"
                  className="bg-charcoal border-slate-800"
                  data-testid="expert-title"
                />
                <p className="text-xs text-slate-500">This is the first thing people will see</p>
              </div>

              {/* Bio */}
              <div className="space-y-2">
                <Label>Bio *</Label>
                <Textarea
                  value={formData.bio}
                  onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                  placeholder="Tell potential clients about your experience, achievements, and what you can help with..."
                  className="bg-charcoal border-slate-800 min-h-[150px]"
                  data-testid="expert-bio"
                />
              </div>

              {/* Expertise */}
              <div className="space-y-2">
                <Label>Areas of Expertise *</Label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {formData.expertise.map((exp) => (
                    <Badge key={exp} variant="secondary" className="bg-gold-500/20 text-gold-500 hover:bg-gold-500/30">
                      {exp}
                      <button type="button" onClick={() => removeExpertise(exp)} className="ml-2">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) handleExpertiseSelect(e.target.value);
                  }}
                  className="w-full bg-charcoal border border-slate-800 rounded-sm px-4 py-2 text-white"
                  data-testid="expertise-select"
                >
                  <option value="">Select expertise to add...</option>
                  {categories.expertise.filter(e => !formData.expertise.includes(e)).map((exp) => (
                    <option key={exp} value={exp}>{exp}</option>
                  ))}
                </select>
                {formData.expertise.length === 0 && (
                  <p className="text-xs text-red-400">Please select at least one area of expertise</p>
                )}
              </div>

              {/* Industries */}
              <div className="space-y-2">
                <Label>Industries</Label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {formData.industries.map((ind) => (
                    <Badge key={ind} variant="secondary" className="bg-slate-800 text-slate-300">
                      {ind}
                      <button type="button" onClick={() => removeIndustry(ind)} className="ml-2">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) handleIndustrySelect(e.target.value);
                  }}
                  className="w-full bg-charcoal border border-slate-800 rounded-sm px-4 py-2 text-white"
                  data-testid="industry-select"
                >
                  <option value="">Select industry to add...</option>
                    {categories.industries.filter(i => !formData.industries.includes(i)).map((ind) => (
                      <option key={ind} value={ind}>{ind}</option>
                    ))}
                  </select>
              </div>

              {/* Hourly Rate & Currency */}
              <div className="space-y-2">
                <Label>Hourly Rate *</Label>
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      type="number"
                      value={formData.hourly_rate}
                      onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate: e.target.value }))}
                      placeholder="e.g., 300"
                      className="pl-12 bg-charcoal border-slate-800"
                      data-testid="expert-rate"
                    />
                  </div>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                    className="w-36 bg-charcoal border border-slate-800 rounded-sm px-3 py-2 text-white"
                    data-testid="expert-currency"
                  >
                    {CURRENCIES.map((curr) => (
                      <option key={curr.value} value={curr.value}>{curr.value} ({curr.symbol})</option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-slate-500">Average expert rates range from $200-$800/hour (or equivalent)</p>
              </div>

              {/* Availability / Time Commitment */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gold-500" />
                  How much time do you have?
                </Label>
                <div className="space-y-2">
                  {AVAILABILITY_OPTIONS.map((option) => (
                    <div 
                      key={option.value}
                      onClick={() => toggleAvailability(option.value)}
                      className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-all ${
                        formData.availability.includes(option.value)
                          ? "bg-gold-500/10 border-gold-500"
                          : "bg-charcoal border-slate-800 hover:border-slate-700"
                      }`}
                      data-testid={`availability-${option.value}`}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        formData.availability.includes(option.value)
                          ? "bg-gold-500 border-gold-500"
                          : "border-slate-600"
                      }`}>
                        {formData.availability.includes(option.value) && (
                          <CheckCircle2 className="w-3 h-3 text-black" />
                        )}
                      </div>
                      <span className={formData.availability.includes(option.value) ? "text-gold-400" : "text-slate-300"}>
                        {option.label}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500">Select all that apply. This helps clients understand your availability.</p>
              </div>

              {/* Meeting Link */}
              <div className="space-y-2">
                <Label>Meeting Link (Optional)</Label>
                <Input
                  value={formData.meeting_link}
                  onChange={(e) => setFormData(prev => ({ ...prev, meeting_link: e.target.value }))}
                  placeholder="e.g., https://calendly.com/yourname"
                  className="bg-charcoal border-slate-800"
                />
              </div>

              {/* LinkedIn */}
              <div className="space-y-2">
                <Label>LinkedIn URL (Optional)</Label>
                <Input
                  value={formData.linkedin}
                  onChange={(e) => setFormData(prev => ({ ...prev, linkedin: e.target.value }))}
                  placeholder="https://linkedin.com/in/..."
                  className="bg-charcoal border-slate-800"
                />
              </div>

              {/* Timezone */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-gold-500" />
                  Your Timezone
                </Label>
                <select
                  value={formData.timezone}
                  onChange={(e) => setFormData(prev => ({ ...prev, timezone: e.target.value }))}
                  className="w-full bg-charcoal border border-slate-800 rounded-sm px-4 py-3 text-white"
                  data-testid="timezone-select"
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500">Clients will see your timezone to schedule calls at convenient times</p>
              </div>

              {/* Google Calendar Connection */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gold-500" />
                  Calendar Integration
                </Label>
                <div className="bg-charcoal border border-slate-800 p-4 rounded-sm">
                  {formData.google_calendar_connected ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                        <span className="text-green-400">Google Calendar Connected</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setFormData(prev => ({ ...prev, google_calendar_connected: false }))}
                        className="text-red-400 hover:text-red-300"
                      >
                        Disconnect
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-slate-400">Connect your Google Calendar to automatically sync your availability</p>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleConnectCalendar}
                        disabled={calendarConnecting || !user}
                        className="border-slate-700 hover:bg-slate-800"
                        data-testid="connect-calendar-btn"
                      >
                        {calendarConnecting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Connecting...
                          </>
                        ) : (
                          <>
                            <Calendar className="w-4 h-4 mr-2" />
                            Connect to Google Calendar
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Stay Anonymous */}
              <div className="space-y-3">
                <div className="bg-charcoal border border-slate-800 p-4 rounded-sm">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="stay-anonymous"
                      checked={formData.stay_anonymous}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, stay_anonymous: checked }))}
                      className="mt-1 border-slate-600 data-[state=checked]:bg-gold-500 data-[state=checked]:border-gold-500"
                      data-testid="stay-anonymous-checkbox"
                    />
                    <div className="flex-1">
                      <Label htmlFor="stay-anonymous" className="flex items-center gap-2 cursor-pointer">
                        <EyeOff className="w-4 h-4 text-gold-500" />
                        Stay Anonymous
                      </Label>
                      <p className="text-xs text-slate-500 mt-1">
                        Your name, photo, and company details will be hidden from public view. 
                        Clients will see you as "Anonymous Expert" with your expertise and rates visible.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Profile Image */}
              <div className="space-y-4">
                <Label>Profile Photo</Label>
                
                {/* Image Preview */}
                {formData.image_url && (
                  <div className="flex items-center gap-4">
                    <img 
                      src={formData.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${formData.image_url}` : formData.image_url}
                      alt="Profile preview"
                      className="w-20 h-20 rounded-full object-cover border-2 border-gold-500"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, image_url: "" }))}
                      className="text-red-400 hover:text-red-300 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                )}

                {/* Upload Options */}
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setImageOption("upload")}
                    className={`px-4 py-2 rounded-sm text-sm transition-colors ${
                      imageOption === "upload" 
                        ? "bg-gold-500 text-black" 
                        : "bg-charcoal border border-slate-700 text-slate-400"
                    }`}
                  >
                    <Upload className="w-4 h-4 inline mr-2" />
                    Upload Photo
                  </button>
                  <button
                    type="button"
                    onClick={() => setImageOption("url")}
                    className={`px-4 py-2 rounded-sm text-sm transition-colors ${
                      imageOption === "url" 
                        ? "bg-gold-500 text-black" 
                        : "bg-charcoal border border-slate-700 text-slate-400"
                    }`}
                  >
                    <Image className="w-4 h-4 inline mr-2" />
                    Image URL
                  </button>
                </div>

                {imageOption === "upload" ? (
                  <div className="space-y-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="w-full border-slate-700 border-dashed py-8"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-5 h-5 mr-2" />
                          Click to upload photo
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-slate-500">JPEG, PNG, WebP, or GIF. Max 5MB.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Input
                      value={formData.image_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                      placeholder="https://example.com/your-photo.jpg"
                      className="bg-charcoal border-slate-800"
                    />
                    <p className="text-xs text-slate-500">Link to a professional headshot (square format recommended)</p>
                  </div>
                )}
              </div>

              <Button 
                type="submit"
                disabled={loading || !user}
                className="w-full bg-gold-500 hover:bg-gold-400 text-black font-semibold py-6 rounded-sm"
                data-testid="submit-expert-profile"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    Create Expert Profile
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
